"use client"

import { useApi, API_ENDPOINTS } from "@/lib/api"
import { useAtsSource } from "@/contexts/ats-source-context"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import type {
  AtsEntitySchema,
  AtsEntityTypeInfo,
  AtsExplorerFilter,
  AtsListRequest,
  AtsMappedPullRequest,
  AtsMappedPullResult,
  AtsPullRequest,
  AtsRecordPage,
  AtsReferenceDataItem,
  AtsSchemaRequest,
} from "@/types/ats-explorer-api"

/**
 * The ATS system/source the source-scoped surfaces operate against. Adapts the shared
 * AtsSourceContext (the header source switcher) so every tool follows the user's
 * selected source rather than only the tenant's active source. atsSystem stays null
 * until sources load (or when none exist / a source lacks a parent system), which
 * consumers already gate on.
 */
export function useTenantAtsSystem(): { atsSystem: string | null; atsSource: string | null; error: string | null } {
  const { selectedSource, selectedAtsSystem, error, isLoading } = useAtsSource()
  const { t } = useTranslation()

  if (!isLoading && selectedSource && !selectedAtsSystem) {
    // A source row without a parent ATS system (legacy NULL ParentId) would otherwise
    // leave consumers in a permanent loading state with no message
    return {
      atsSystem: null,
      atsSource: selectedSource.code,
      error: t("explorer.errors.noAtsSystem", { code: selectedSource.code }),
    }
  }

  return { atsSystem: selectedAtsSystem, atsSource: selectedSource?.code ?? null, error }
}

// Session caches for vendor catalog data, shared across all useExplorer consumers.
// Keyed by ATS SYSTEM — both are resolved server-side from the active SOURCE's
// credentials, so a source switch must clear them (see ats-sources-page).
const referenceDataCache = new Map<string, Promise<AtsReferenceDataItem[]>>()
export const entityTypeDetailCache = new Map<string, Promise<AtsEntityTypeInfo>>()

export function invalidateReferenceDataCache() {
  referenceDataCache.clear()
  entityTypeDetailCache.clear()
}

export function useExplorer() {
  const { atsIntegrationApi } = useApi()

  const loadEntityTypes = async (atsSystem: string): Promise<AtsEntityTypeInfo[]> => {
    return atsIntegrationApi.get<AtsEntityTypeInfo[]>(API_ENDPOINTS.ATS_INTEGRATION.EXPLORER_ENTITY_TYPES(atsSystem))
  }

  /** One entity type with its complete filter set (the listing carries curated filters only) */
  const loadEntityType = async (atsSystem: string, entityType: string): Promise<AtsEntityTypeInfo> => {
    return atsIntegrationApi.get<AtsEntityTypeInfo>(API_ENDPOINTS.ATS_INTEGRATION.EXPLORER_ENTITY_TYPE(atsSystem, entityType))
  }

  const listRecords = async (atsSystem: string, request: AtsListRequest): Promise<AtsRecordPage> => {
    return atsIntegrationApi.post<AtsRecordPage>(API_ENDPOINTS.ATS_INTEGRATION.EXPLORER_RECORDS(atsSystem), request)
  }

  const getRawRecord = async (
    atsSystem: string,
    entityType: string,
    id: string
  ): Promise<Record<string, unknown>> => {
    return atsIntegrationApi.get<Record<string, unknown>>(
      API_ENDPOINTS.ATS_INTEGRATION.EXPLORER_RAW_RECORD(atsSystem, entityType, id)
    )
  }

  const getSchema = async (
    atsSystem: string,
    entityType: string,
    sampleSize = 5,
    filters: AtsExplorerFilter[] = [],
    expandPaths: string[] = []
  ): Promise<AtsEntitySchema> => {
    const request: AtsSchemaRequest = { sampleSize, filters, expandPaths }
    return atsIntegrationApi.post<AtsEntitySchema>(API_ENDPOINTS.ATS_INTEGRATION.EXPLORER_SCHEMA(atsSystem, entityType), request)
  }

  const loadReferenceDataTypes = async (atsSystem: string): Promise<string[]> => {
    return atsIntegrationApi.get<string[]>(API_ENDPOINTS.ATS_INTEGRATION.EXPLORER_REFERENCE_DATA_TYPES(atsSystem))
  }

  /**
   * A reference set, optionally narrowed by a typeahead filter (vendors clamp large
   * sets — Bullhorn options at 300 — so pickers ask the server for matches instead)
   */
  const loadReferenceData = async (atsSystem: string, referenceType: string, filter?: string): Promise<AtsReferenceDataItem[]> => {
    // Session cache (failure-evicting) — the browser, filter bars, and value mapping
    // dialogs all request the same small sets; the server caches 15 min anyway
    const normalizedFilter = filter?.trim().toLowerCase() ?? ""
    const key = `${atsSystem}:${referenceType}:${normalizedFilter}`
    const url = API_ENDPOINTS.ATS_INTEGRATION.EXPLORER_REFERENCE_DATA(atsSystem, referenceType)
    let cached = referenceDataCache.get(key)
    if (!cached) {
      cached = atsIntegrationApi
        .get<AtsReferenceDataItem[]>(normalizedFilter ? `${url}?filter=${encodeURIComponent(normalizedFilter)}` : url)
        .catch((err) => {
          referenceDataCache.delete(key)
          throw err
        })
      referenceDataCache.set(key, cached)
    }
    return cached
  }

  /**
   * Server streams the file; returns a Blob plus a suggested filename. CSV responses
   * pass through fetchWithAuth as raw Response objects (non-JSON content type); JSON
   * responses arrive parsed and are re-serialized, and the parsed list is handed back
   * as `records` so a caller laying it out itself need not parse the file again.
   */
  const pull = async (atsSystem: string, request: AtsPullRequest): Promise<{ blob: Blob; fileName: string; records: unknown[] | null }> => {
    const fileName = `${atsSystem.toLowerCase()}-${request.entityType.toLowerCase()}.${request.format === "Json" ? "json" : "csv"}`
    const result = await atsIntegrationApi.post<Response | unknown>(
      API_ENDPOINTS.ATS_INTEGRATION.EXPLORER_PULL(atsSystem),
      request
    )

    if (result instanceof Response) {
      return { blob: await result.blob(), fileName, records: null }
    }

    return {
      blob: new Blob([JSON.stringify(result, null, 2)], { type: "application/json" }),
      fileName,
      records: Array.isArray(result) ? result : null,
    }
  }

  const mappedPull = async (atsSystem: string, request: AtsMappedPullRequest): Promise<AtsMappedPullResult> => {
    return atsIntegrationApi.post<AtsMappedPullResult>(
      API_ENDPOINTS.ATS_INTEGRATION.EXPLORER_MAPPED_PULL(atsSystem),
      request
    )
  }

  return { loadEntityTypes, loadEntityType, listRecords, getRawRecord, getSchema, loadReferenceDataTypes, loadReferenceData, pull, mappedPull }
}

/** Triggers a browser download for a blob */
export function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}
